import { Link } from "react-router-dom";
import type { EpisodeResponse, EpisodeStatus } from "../../types/api";
import styles from "./EpisodeRow.module.css";

interface EpisodeRowProps {
  episode: EpisodeResponse;
  organizationId: string;
  podcastId: string;
}

function formatDuration(seconds: number | null): string {
  if (!seconds) return "--:--";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }
  return `${minutes}:${secs.toString().padStart(2, "0")}`;
}

function formatDate(dateString: string | null): string {
  if (!dateString) return "—";
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function getStatusLabel(status: EpisodeStatus): string {
  switch (status) {
    case "draft":
      return "Draft";
    case "scheduled":
      return "Scheduled";
    case "published":
      return "Published";
    default:
      return status;
  }
}

export function EpisodeRow({ episode, organizationId, podcastId }: EpisodeRowProps) {
  const episodeUrl = `/organizations/${organizationId}/podcasts/${podcastId}/episodes/${episode.id}`;

  return (
    <div className={styles.row}>
      <div className={styles.mainInfo}>
        <div className={styles.titleSection}>
          <Link to={episodeUrl} className={styles.title}>
            {episode.episodeNumber && (
              <span className={styles.episodeNumber}>#{episode.episodeNumber}</span>
            )}
            {episode.title}
          </Link>
          <div className={styles.badges}>
            <span className={`${styles.statusBadge} ${styles[episode.status]}`}>
              {getStatusLabel(episode.status)}
            </span>
            {episode.isPremium && <span className={styles.premiumBadge}>Premium</span>}
            {episode.explicit && <span className={styles.explicitBadge}>E</span>}
          </div>
        </div>
        {episode.description && <p className={styles.description}>{episode.description}</p>}
      </div>

      <div className={styles.meta}>
        <div className={styles.metaItem}>
          <span className={styles.metaLabel}>Duration</span>
          <span className={styles.metaValue}>{formatDuration(episode.audioDurationSeconds)}</span>
        </div>
        <div className={styles.metaItem}>
          <span className={styles.metaLabel}>
            {episode.status === "scheduled" ? "Scheduled" : "Published"}
          </span>
          <span className={styles.metaValue}>
            {episode.status === "scheduled"
              ? formatDate(episode.scheduledAt)
              : formatDate(episode.publishedAt)}
          </span>
        </div>
        {episode.seasonNumber && (
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>Season</span>
            <span className={styles.metaValue}>{episode.seasonNumber}</span>
          </div>
        )}
      </div>

      <div className={styles.actions}>
        <Link to={`${episodeUrl}/edit`} className={styles.editLink}>
          Edit
        </Link>
      </div>
    </div>
  );
}
